/**
 * Cart Drawer JavaScript
 * Handles mini cart drawer functionality with Shopify Cart API
 */

class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.toggleButtons = document.querySelectorAll('[data-cart-drawer-toggle]');
    this.closeButtons = document.querySelectorAll('[data-cart-drawer-close]');
    this.backdrop = this.drawer?.querySelector('[data-cart-drawer-backdrop]');
    this.body = this.drawer?.querySelector('[data-cart-drawer-body]');
    this.empty = this.drawer?.querySelector('[data-cart-drawer-empty]');
    this.items = this.drawer?.querySelector('[data-cart-drawer-items]');
    this.total = this.drawer?.querySelector('[data-cart-drawer-total]');
    this.cartCount = document.querySelector('[data-cart-count]');
    this.crossSell = this.drawer?.querySelector('[data-cross-sell]');
    this.crossSellItems = this.drawer?.querySelector('[data-cross-sell-items]');
    
    if (!this.drawer) return;
    
    // Store instance globally for external access
    window.cartDrawer = this;
    
    this.init();
  }

  init() {
    // Toggle drawer
    this.toggleButtons.forEach(button => {
      button.addEventListener('click', () => this.open());
    });

    // Close drawer
    this.closeButtons.forEach(button => {
      button.addEventListener('click', () => this.close());
    });

    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.close());
    }

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    // Listen for add to cart events
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action*="/cart/add"]');
      if (form) {
        e.preventDefault();
        this.handleAddToCart(form);
      }
    });

    document.addEventListener('click', (e) => {
      const button = e.target.closest('[data-cross-sell-add]');
      if (!button) return;
      e.preventDefault();
      this.addCrossSell(button);
    });

    // Load initial cart
    this.updateCart();
  }

  isOpen() {
    return this.drawer?.classList.contains('cart-drawer--open');
  }

  open() {
    if (!this.drawer) return;
    this.drawer.classList.add('cart-drawer--open');
    document.body.style.overflow = 'hidden';
    this.updateCart();
  }

  close() {
    if (!this.drawer) return;
    this.drawer.classList.remove('cart-drawer--open');
    document.body.style.overflow = '';
  }

  async updateCart() {
    if (!this.drawer) return;
    
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      this.updateCartCount(cart.item_count);
      this.updateCartItems(cart);
      this.updateCartTotal(cart.total_price);
      this.loadCrossSell(cart);
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  updateCartCount(count) {
    if (!this.cartCount) return;
    
    if (count > 0) {
      this.cartCount.textContent = count;
    } else {
      this.cartCount.textContent = '';
    }
  }

  updateCartItems(cart) {
    if (!this.items || !this.empty || !this.body) return;

    if (cart.items.length === 0) {
      this.empty.classList.add('cart-drawer__empty--visible');
      this.body.style.display = 'none';
      return;
    }

    this.empty.classList.remove('cart-drawer__empty--visible');
    this.body.style.display = 'block';

    this.items.innerHTML = cart.items.map(item => this.renderCartItem(item)).join('');
    
    // Attach quantity handlers
    this.attachQuantityHandlers();
  }

  renderCartItem(item) {
    const variantTitle = item.variant_title && item.variant_title !== 'Default Title' 
      ? `<div class="cart-drawer__item-variant">${item.variant_title}</div>` 
      : '';
    
    return `
      <div class="cart-drawer__item" data-line-item="${item.key}">
        <div class="cart-drawer__item-image">
          <img 
            src="${item.image}" 
            alt="${item.product_title}" 
            class="cart-drawer__item-img"
            loading="lazy"
          >
        </div>
        <div class="cart-drawer__item-info">
          <h3 class="cart-drawer__item-title">
            <a href="${item.url}">${item.product_title}</a>
          </h3>
          ${variantTitle}
          <div class="cart-drawer__item-bottom">
            <div class="cart-drawer__item-price">${this.formatMoney(item.line_price)}</div>
            <div class="cart-drawer__item-controls">
              <div class="cart-drawer__item-quantity">
                <button 
                  type="button" 
                  class="cart-drawer__item-qty-btn" 
                  data-action="decrease" 
                  data-key="${item.key}"
                  aria-label="Decrease quantity"
                >−</button>
                <input 
                  type="number" 
                  class="cart-drawer__item-qty-input" 
                  value="${item.quantity}" 
                  min="1"
                  data-key="${item.key}"
                  aria-label="Quantity"
                >
                <button 
                  type="button" 
                  class="cart-drawer__item-qty-btn" 
                  data-action="increase" 
                  data-key="${item.key}"
                  aria-label="Increase quantity"
                >+</button>
              </div>
              <button 
                type="button" 
                class="cart-drawer__item-remove" 
                data-action="remove"
                data-key="${item.key}"
                aria-label="Remove item"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  attachQuantityHandlers() {
    const actionButtons = this.items?.querySelectorAll('[data-action]');
    const qtyInputs = this.items?.querySelectorAll('[data-key] input[type="number"]');

    actionButtons?.forEach(button => {
      button.addEventListener('click', async (e) => {
        const action = button.dataset.action;
        const key = button.dataset.key;

        if (action === 'remove') {
          await this.removeItem(key);
          return;
        }

        const input = this.items.querySelector(`input[data-key="${key}"]`);
        let qty = parseInt(input.value) || 1;

        if (action === 'increase') {
          qty += 1;
        } else if (action === 'decrease' && qty > 1) {
          qty -= 1;
        }

        await this.updateQuantity(key, qty);
      });
    });

    qtyInputs?.forEach(input => {
      input.addEventListener('change', async (e) => {
        const key = input.dataset.key;
        const qty = parseInt(input.value) || 1;
        await this.updateQuantity(key, qty);
      });
    });
  }

  async removeItem(key) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: key, quantity: 0 })
      });

      if (response.ok) {
        await this.updateCart();
      }
    } catch (error) {
      console.error('Error removing item:', error);
    }
  }

  async updateQuantity(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: key, quantity })
      });

      if (response.ok) {
        await this.updateCart();
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
    }
  }

  updateCartTotal(total) {
    if (this.total) {
      this.total.textContent = this.formatMoney(total);
    }
  }

  async handleAddToCart(form) {
    const formData = new FormData(form);
    const data = Object.fromEntries(formData);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        await this.updateCart();
        this.open();
      } else {
        const error = await response.json();
        alert(error.description || (window.themeStrings && window.themeStrings.addError) || '');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert((window.themeStrings && window.themeStrings.addError) || '');
    }
  }

  formatMoney(cents) {
    if (window.themeFormatMoney) return window.themeFormatMoney(cents);
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.themeCurrency || 'USD'
    }).format(cents / 100);
  }

  loadCrossSell(cart) {
    const targets = document.querySelectorAll('[data-cross-sell]');
    const items = cart && cart.items ? cart.items : [];
    const cartIds = items.map(function(item) { return String(item.product_id); });
    const uniqueIds = Array.from(new Set(cartIds)).slice(0, 4);

    if (!uniqueIds.length || !targets.length) {
      targets.forEach(function(target) { target.hidden = true; });
      return;
    }

    Promise.all(uniqueIds.map(function(id) {
      return fetch('/recommendations/products.json?product_id=' + id + '&limit=4&intent=related')
        .then(function(response) { return response.ok ? response.json() : { products: [] }; })
        .catch(function() { return { products: [] }; });
    })).then((results) => {
      const seen = new Set(cartIds);
      const products = [];
      results.forEach(function(data) {
        (data.products || []).forEach(function(product) {
          const id = String(product.id);
          if (seen.has(id)) return;
          seen.add(id);
          products.push(product);
        });
      });
      const picked = products.slice(0, 4);
      targets.forEach((target) => {
        const list = target.querySelector('[data-cross-sell-items]');
        if (!list || !picked.length) {
          target.hidden = true;
          return;
        }
        list.innerHTML = picked.map((product) => this.renderCrossSellItem(product)).join('');
        target.hidden = false;
      });
    }).catch(function() {
      targets.forEach(function(target) { target.hidden = true; });
    });
  }

  renderCrossSellItem(product) {
    const variant = (product.variants || []).find((item) => item.available);
    const image = product.featured_image || '';
    const title = this.escapeHtml(product.title);
    const url = this.escapeHtml(product.url || '#');
    const price = this.escapeHtml(this.formatMoney(product.price));
    const label = (window.themeStrings && window.themeStrings.addToCart) || 'Sepete Ekle';
    const soldOut = (window.themeStrings && window.themeStrings.soldOut) || '';
    const action = variant
      ? '<button type="button" class="cart-cross-sell__add" data-cross-sell-add data-variant-id="' + variant.id + '">' + this.escapeHtml(label) + '</button>'
      : '<span class="cart-cross-sell__sold-out">' + this.escapeHtml(soldOut) + '</span>';

    return '<div class="cart-cross-sell__item">' +
      '<a class="cart-cross-sell__media" href="' + url + '">' +
        (image ? '<img src="' + this.escapeHtml(image) + '" alt="" width="64" height="80">' : '') +
      '</a>' +
      '<div class="cart-cross-sell__info">' +
        '<a class="cart-cross-sell__name" href="' + url + '">' + title + '</a>' +
        '<span class="cart-cross-sell__price">' + price + '</span>' +
      '</div>' +
      action +
    '</div>';
  }

  async addCrossSell(button) {
    const id = button.dataset.variantId;
    if (!id || button.disabled) return;

    button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: Number(id), quantity: 1 })
      });

      if (response.ok) {
        await this.updateCart();
        this.open();
        return;
      }

      const error = await response.json();
      alert(error.description || (window.themeStrings && window.themeStrings.addError) || '');
      button.disabled = false;
    } catch (error) {
      console.error('Error adding cross-sell item:', error);
      alert((window.themeStrings && window.themeStrings.addError) || '');
      button.disabled = false;
    }
  }

  escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[char]));
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CartDrawer());
} else {
  new CartDrawer();
}

